<template>
  <div>
    <div v-if="loading && !pages.length" class="px-4 py-3">
      <div class="animate-pulse space-y-2">
        <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
      </div>
    </div>

    <div v-else>
      <div v-if="pages.length > 0" class="border-t border-gray-100 dark:border-gray-700">
        <div class="px-4 py-2 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
          <input
            v-model="search"
            type="text"
            placeholder="Search pages..."
            class="text-xs border-2 border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 w-48 focus:ring-primary-500 focus:border-primary-500"
            @input="debouncedFetch">
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-400">
              {{ extractedCount }} / {{ pages.length }} extracted
            </span>
            <button
              @click="$emit('add-page')"
              class="text-xs text-primary-600 dark:text-primary-400 hover:underline">
              + Add Page
            </button>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-xs table-fixed min-w-[960px]">
            <thead>
              <tr class="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th class="px-2 py-2 font-medium w-8"></th>
                <th class="px-2 py-2 font-medium cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none" @click="toggleSort('title')">
                  Title
                  <span v-if="sortField === 'title'" class="ml-0.5">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th class="px-2 py-2 font-medium">Slug</th>
                <th class="px-2 py-2 font-medium w-16">Type</th>
                <th class="px-2 py-2 font-medium w-8" title="Status">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" /></svg>
                </th>
                <th class="px-2 py-2 font-medium w-[104px]" title="Optimization pipeline progress">Pipeline</th>
                <th class="px-2 py-2 font-medium text-right w-14 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none" @click="toggleSort('sessions')">
                  <span v-if="sortField === 'sessions'" class="mr-0.5">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>Sess
                </th>
                <th class="px-2 py-2 font-medium text-right w-14 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none" @click="toggleSort('organic')">
                  <span v-if="sortField === 'organic'" class="mr-0.5">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>Org
                </th>
                <th class="px-2 py-2 font-medium text-right w-14">PV</th>
                <th class="px-2 py-2 font-medium text-right w-14 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none" @click="toggleSort('clicks')">
                  <span v-if="sortField === 'clicks'" class="mr-0.5">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>Clicks
                </th>
                <th class="px-2 py-2 font-medium text-right w-14 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none" @click="toggleSort('impressions')">
                  <span v-if="sortField === 'impressions'" class="mr-0.5">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>Impr
                </th>
                <th class="px-2 py-2 font-medium text-right w-14 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none" @click="toggleSort('priority')">
                  <span v-if="sortField === 'priority'" class="mr-0.5">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>Pri
                </th>
                <th class="px-2 py-2 font-medium w-14">Health</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="page in sortedPages" :key="page.id">
                <!-- Main Row -->
                <tr
                  class="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer"
                  @click="$emit('view-page', page)">
                  <td class="px-2 py-2" @click.stop>
                    <button
                      v-if="page.pipeline_status"
                      @click="toggleExpand(page.id)"
                      class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-transform"
                      :class="{ 'rotate-90': expandedRows[page.id] }">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </td>
                  <td class="px-2 py-2 text-primary-600 dark:text-primary-400 font-medium truncate" :title="page.title">
                    {{ page.title }}
                  </td>
                  <td class="px-2 py-2 text-gray-500 dark:text-gray-400 font-mono truncate" :title="page.slug">
                    {{ page.slug }}
                  </td>
                  <td class="px-2 py-2">
                    <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                      {{ formatPageType(page.page_type) }}
                    </span>
                  </td>
                  <td class="px-2 py-2" :title="page.status">
                    <span v-if="page.status === 'published'" class="text-green-500">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                    </span>
                    <span v-else-if="page.status === 'draft'" class="text-amber-400">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" clip-rule="evenodd" /></svg>
                    </span>
                    <span v-else-if="page.status === 'archived'" class="text-gray-400">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" /><path fill-rule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clip-rule="evenodd" /></svg>
                    </span>
                    <span v-else class="text-gray-400">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="10" r="8" /></svg>
                    </span>
                  </td>
                  <td class="px-2 py-2">
                    <div v-if="page.pipeline_status" class="flex items-center gap-0.5" :title="pipelineTooltip(page.pipeline_status)">
                      <div
                        v-for="step in pipelineSteps"
                        :key="step.key"
                        class="w-2 h-2 rounded-full border"
                        :class="stepDotClass(page.pipeline_status[step.key])" />
                      <span class="text-[10px] text-gray-400 ml-1 tabular-nums">{{ pipelineCompletedCount(page.pipeline_status) }}/{{ pipelineSteps.length }}</span>
                    </div>
                    <span v-else class="text-gray-400 text-xs">&mdash;</span>
                  </td>
                  <td class="px-2 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                    <template v-if="page.traffic_summary">
                      {{ formatNumber(page.traffic_summary.sessions) }}
                    </template>
                    <span v-else-if="!page.ga_enabled" class="text-gray-400">&mdash;</span>
                    <span v-else class="text-gray-400">0</span>
                  </td>
                  <td class="px-2 py-2 text-right tabular-nums">
                    <template v-if="page.organic_sessions != null">
                      <span :class="page.organic_sessions > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'">
                        {{ formatNumber(page.organic_sessions) }}
                      </span>
                    </template>
                    <span v-else-if="!page.ga_enabled" class="text-gray-400">&mdash;</span>
                    <span v-else class="text-gray-400">0</span>
                  </td>
                  <td class="px-2 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                    <template v-if="page.traffic_summary">
                      {{ formatNumber(page.traffic_summary.page_views) }}
                    </template>
                    <span v-else-if="!page.ga_enabled" class="text-gray-400">&mdash;</span>
                    <span v-else class="text-gray-400">0</span>
                  </td>
                  <td class="px-2 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                    <template v-if="page.gsc_summary">
                      {{ formatNumber(page.gsc_summary.clicks) }}
                    </template>
                    <span v-else-if="!page.gsc_enabled" class="text-gray-400">&mdash;</span>
                    <span v-else class="text-gray-400">0</span>
                  </td>
                  <td class="px-2 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                    <template v-if="page.gsc_summary">
                      {{ formatNumber(page.gsc_summary.impressions) }}
                    </template>
                    <span v-else-if="!page.gsc_enabled" class="text-gray-400">&mdash;</span>
                    <span v-else class="text-gray-400">0</span>
                  </td>
                  <td class="px-2 py-2 text-right">
                    <button
                      v-if="page.priority_score_data"
                      @click.stop="showPriorityPopover(page, $event)"
                      class="inline-flex items-center gap-1 hover:opacity-80 cursor-pointer">
                      <span class="w-2 h-2 rounded-full" :class="priorityDotClass(page.priority_score_data.score)"></span>
                      <span class="text-xs font-medium tabular-nums" :class="priorityTextClass(page.priority_score_data.score)">
                        {{ Math.round(page.priority_score_data.score) }}
                      </span>
                    </button>
                    <span v-else class="text-gray-400 text-xs">&mdash;</span>
                  </td>
                  <td class="px-2 py-2">
                    <span v-if="page.latest_audit_score != null" class="inline-flex items-center gap-1">
                      <span class="w-2 h-2 rounded-full" :class="healthDotClass(page.latest_audit_score)"></span>
                      <span class="text-xs font-medium" :class="healthTextClass(page.latest_audit_score)">{{ page.latest_audit_score }}</span>
                    </span>
                    <span v-else class="text-gray-400 text-xs">&mdash;</span>
                  </td>
                </tr>

                <!-- Expanded Sub-Row -->
                <tr v-if="expandedRows[page.id] && page.pipeline_status" class="bg-gray-50/50 dark:bg-gray-800/20 border-b border-gray-100 dark:border-gray-700">
                  <td colspan="13" class="px-2 py-2.5">
                    <div class="flex items-center gap-6 text-[11px]">
                      <!-- Sync Status -->
                      <div class="flex flex-col gap-1 shrink-0">
                        <div class="flex items-center gap-1.5">
                          <span class="w-1.5 h-1.5 rounded-full" :class="page.ga_enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'"></span>
                          <span class="text-gray-500 dark:text-gray-400">GA4</span>
                          <span v-if="page.ga_enabled" class="text-gray-400">
                            {{ page.ga_last_sync_date ? timeAgo(page.ga_last_sync_date) : 'no data' }}
                          </span>
                          <span v-else class="text-gray-400">disabled</span>
                        </div>
                        <div class="flex items-center gap-1.5">
                          <span class="w-1.5 h-1.5 rounded-full" :class="page.gsc_enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'"></span>
                          <span class="text-gray-500 dark:text-gray-400">GSC</span>
                          <span v-if="page.gsc_enabled" class="text-gray-400">
                            {{ page.gsc_last_sync_date ? timeAgo(page.gsc_last_sync_date) : 'no data' }}
                          </span>
                          <span v-else class="text-gray-400">disabled</span>
                        </div>
                      </div>

                      <!-- Pipeline Stepper -->
                      <div class="flex items-center gap-1 shrink-0" :title="pipelineTooltip(page.pipeline_status)">
                        <div
                          v-for="step in pipelineSteps"
                          :key="step.key"
                          :title="`${step.label}: ${page.pipeline_status[step.key]?.replace('_', ' ') || 'not started'}`"
                          class="w-2.5 h-2.5 rounded-full border cursor-default transition-colors"
                          :class="stepDotClass(page.pipeline_status[step.key])" />
                      </div>

                      <!-- Pipeline Legend (compact) -->
                      <div class="text-[10px] text-gray-400 shrink-0">
                        {{ pipelineSummary(page.pipeline_status) }}
                      </div>

                      <!-- Spacer -->
                      <div class="flex-1 min-w-0" />

                      <!-- Audit Summary -->
                      <div class="text-right shrink-0">
                        <template v-if="page.latest_audit_score != null">
                          <span class="font-medium" :class="healthTextClass(page.latest_audit_score)">
                            {{ page.latest_audit_score }}
                          </span>
                          <span v-if="page.latest_audit_at" class="text-gray-400 ml-1">
                            {{ timeAgo(page.latest_audit_at) }}
                          </span>
                        </template>
                        <span v-else class="text-gray-400">No audit</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 text-center border-t border-gray-100 dark:border-gray-700">
        No pages yet.
        <button @click="$emit('add-page')" class="text-primary-600 dark:text-primary-400 hover:underline">
          Add one
        </button>
        or import from sitemap.
      </div>
    </div>

    <!-- Priority Score Popover -->
    <div
      v-if="popoverPage?.priority_score_data"
      :style="popoverStyle"
      class="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 w-72 text-xs"
      @click.stop>
      <div class="flex items-center justify-between mb-2">
        <span class="font-semibold text-gray-800 dark:text-gray-200">Priority Score</span>
        <button @click="closePriorityPopover" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-base leading-none">&times;</button>
      </div>
      <div class="flex items-center gap-2 mb-3">
        <span class="text-2xl font-bold" :class="priorityTextClass(popoverPage.priority_score_data.score)">
          {{ Math.round(popoverPage.priority_score_data.score) }}
        </span>
        <span class="text-gray-400">/ 100</span>
        <span class="text-[10px] text-gray-400 ml-auto">
          {{ popoverPage.priority_score_data.signals_available }}/6 signals
        </span>
      </div>
      <div class="space-y-1.5">
        <div v-for="signal in signalList" :key="signal.key" class="flex items-center gap-2">
          <span class="w-16 text-gray-500 dark:text-gray-400 shrink-0">{{ signal.label }}</span>
          <div class="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all"
              :class="signalBarClass(popoverPage.priority_score_data.signals[signal.key])"
              :style="{ width: (popoverPage.priority_score_data.signals[signal.key] ?? 0) + '%' }" />
          </div>
          <span class="w-6 text-right tabular-nums text-gray-500">
            {{ popoverPage.priority_score_data.signals[signal.key] != null ? Math.round(popoverPage.priority_score_data.signals[signal.key]) : '—' }}
          </span>
        </div>
      </div>
      <div class="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-400">
        <div v-if="popoverPage.priority_score_data.keyword">
          Keyword: <span class="text-gray-600 dark:text-gray-300">{{ popoverPage.priority_score_data.keyword }}</span>
        </div>
        <div v-if="popoverPage.priority_score_data.computed_at">
          Computed: {{ timeAgo(popoverPage.priority_score_data.computed_at) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { getWebsitePages } from '~/javascript/api/website_pages.js'

const props = defineProps({
  websiteId: { type: String, required: true },
  dateRange: { type: Object, default: null }
})

defineEmits(['add-page', 'view-page'])

const pages = ref([])
const loading = ref(true)
const search = ref('')
const expandedRows = reactive({})
const sortField = ref(null)
const sortDir = ref('desc')
let debounceTimer = null
let fetchSeq = 0

const extractedCount = computed(() => pages.value.filter((p) => p.extracted).length)

const sortValue = (item, field) => {
  const fieldMap = {
    sessions: item.traffic_summary?.sessions,
    organic: item.organic_sessions,
    clicks: item.gsc_summary?.clicks,
    impressions: item.gsc_summary?.impressions,
    priority: item.priority_score_data?.score
  }
  return fieldMap[field] ?? -1
}

const sortedPages = computed(() => {
  if (!sortField.value) return pages.value
  const field = sortField.value
  const dir = sortDir.value === 'asc' ? 1 : -1
  return [...pages.value].sort((a, b) => {
    if (field === 'title') {
      const ta = (a.title || '').toLowerCase()
      const tb = (b.title || '').toLowerCase()
      if (ta < tb) return -dir
      if (ta > tb) return dir
      return 0
    }
    const va = sortValue(a, field)
    const vb = sortValue(b, field)
    return (va - vb) * dir
  })
})

const pipelineSteps = [
  { key: 'extract', label: 'Extract' },
  { key: 'keywords', label: 'Keywords' },
  { key: 'serp', label: 'SERP' },
  { key: 'competitors', label: 'Competitors' },
  { key: 'strategy', label: 'Strategy' },
  { key: 'counting', label: 'Counting' },
  { key: 'topics', label: 'Topics' },
  { key: 'structure', label: 'Structure' },
  { key: 'content', label: 'Content' },
  { key: 'density', label: 'Density' },
  { key: 'faq_schema', label: 'FAQ/Schema' },
  { key: 'final_audit', label: 'Final Audit' }
]

const fetchPages = async() => {
  fetchSeq += 1
  const seq = fetchSeq
  try {
    loading.value = true
    const params = {}
    if (search.value) params.q = search.value
    if (props.dateRange) {
      params.start_date = props.dateRange.startDate
      params.end_date = props.dateRange.endDate
    }
    const response = await getWebsitePages(props.websiteId, params)
    if (seq !== fetchSeq) return // stale response, discard
    pages.value = response.data
  } catch (error) {
    if (seq === fetchSeq) console.error('Error fetching pages:', error)
  } finally {
    if (seq === fetchSeq) loading.value = false
  }
}

watch(() => props.dateRange, () => {
  if (props.dateRange) fetchPages()
}, { deep: true })

const debouncedFetch = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(fetchPages, 300)
}

const toggleExpand = (pageId) => {
  expandedRows[pageId] = !expandedRows[pageId]
}

const toggleSort = (field) => {
  if (sortField.value === field) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDir.value = field === 'title' ? 'asc' : 'desc'
  }
}

const formatPageType = (type) => {
  const map = { page: 'Page', location_page: 'Location', service_page: 'Service', service_location_page: 'Svc+Loc' }
  return map[type] || type
}

const formatNumber = (n) => {
  if (!n) return '0'
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return n.toString()
}

const priorityDotClass = (score) => {
  if (score >= 80) return 'bg-green-500'
  if (score >= 50) return 'bg-amber-500'
  return 'bg-gray-400'
}

const priorityTextClass = (score) => {
  if (score >= 80) return 'text-green-600 dark:text-green-400'
  if (score >= 50) return 'text-amber-600 dark:text-amber-400'
  return 'text-gray-500 dark:text-gray-400'
}

const popoverPage = ref(null)
const popoverStyle = ref({})

const showPriorityPopover = (page, event) => {
  if (popoverPage.value?.id === page.id) {
    popoverPage.value = null
    return
  }
  popoverPage.value = page
  const rect = event.currentTarget.getBoundingClientRect()
  popoverStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: 50
  }
}

const closePriorityPopover = () => {
  popoverPage.value = null
}

const signalList = [
  { key: 'volume', label: 'Volume' },
  { key: 'ranking', label: 'Ranking' },
  { key: 'traffic', label: 'Traffic' },
  { key: 'gap', label: 'Gap' },
  { key: 'intent', label: 'Intent' },
  { key: 'content', label: 'Content' }
]

const signalBarClass = (value) => {
  if (value == null) return 'bg-gray-300 dark:bg-gray-600'
  if (value >= 70) return 'bg-green-500'
  if (value >= 40) return 'bg-amber-500'
  return 'bg-red-400'
}

const healthDotClass = (score) => {
  if (score >= 80) return 'bg-green-500'
  if (score >= 50) return 'bg-amber-500'
  return 'bg-red-500'
}

const healthTextClass = (score) => {
  if (score >= 80) return 'text-green-600 dark:text-green-400'
  if (score >= 50) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}

const stepDotClass = (status) => {
  const map = {
    completed: 'bg-green-500 border-green-500',
    in_progress: 'bg-primary-500 border-primary-500',
    failed: 'bg-red-500 border-red-500',
    not_started: 'bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500'
  }
  return map[status] || map.not_started
}

const pipelineCompletedCount = (status) => {
  if (!status) return 0
  return Object.values(status).filter((s) => s === 'completed').length
}

const pipelineSummary = (status) => {
  if (!status) return ''
  return `${pipelineCompletedCount(status)}/${Object.keys(status).length} steps`
}

const pipelineTooltip = (status) => {
  if (!status) return ''
  return pipelineSteps
    .map((s) => `${s.label}: ${(status[s.key] || 'not_started').replace('_', ' ')}`)
    .join('\n')
}

const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const refresh = () => fetchPages()

defineExpose({ refresh })

onMounted(() => {
  fetchPages()
  document.addEventListener('click', closePriorityPopover)
})

onUnmounted(() => {
  document.removeEventListener('click', closePriorityPopover)
})
</script>
